import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

interface PieData {
  name: string;
  value: number;
  color: string;
}

interface SimplePieChartProps {
  title: string;
  description: string;
  data: PieData[];
  id?: string;
}

const shortenLabel = (label: string, maxLength = 22) => {
  if (label.length <= maxLength) return label;
  return `${label.slice(0, maxLength - 1)}...`;
};

export function SimplePieChart({ title, description, data, id }: SimplePieChartProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const topItem = data[0];
  const compactData = data.slice(0, 8);
  const chartData = [...compactData].reverse();

  return (
    <Card id={id} className="overflow-hidden border-border/70 bg-gradient-to-br from-card via-card to-muted/20 shadow-sm">
      <CardHeader className="space-y-3 border-b border-border/60 bg-muted/20 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-lg">{title}</CardTitle>
            <CardDescription className="mt-1 max-w-md">{description}</CardDescription>
          </div>
          <Badge variant="outline" className="bg-background/80 text-xs font-medium">
            {total} total
          </Badge>
        </div>
        {topItem ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: topItem.color }} />
            <span>
              Top segment: <span className="font-medium text-foreground">{topItem.name}</span> ({((topItem.value / total) * 100).toFixed(0)}%)
            </span>
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="p-4 pt-3">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_260px]">
          <div className="relative rounded-2xl border border-border/60 bg-background/70 p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Top segments</div>
              <div className="rounded-xl border border-border/70 bg-background px-3 py-1 text-right shadow-sm">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total</div>
                <div className="text-xl font-semibold tracking-tight">{total}</div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 12, left: 20, bottom: 0 }}>
                <CartesianGrid horizontal={false} strokeDasharray="3 3" className="stroke-border/60" />
                <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={150}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value: string) => shortenLabel(value)}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }}
                  formatter={(value: number, name: string) => {
                    const share = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                    return [`${value} (${share}%)`, name];
                  }}
                />
                <Bar dataKey="value" radius={[0, 10, 10, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-2xl border border-border/60 bg-muted/10 p-3">
            <div className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Segment Breakdown
            </div>
            <div className="space-y-2">
              {compactData.length === 0 ? (
                <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                  No chart data available
                </div>
              ) : (
                compactData.map((item) => {
                  const percentage = total > 0 ? (item.value / total) * 100 : 0;

                  return (
                    <div key={item.name} className="rounded-xl border border-border/60 bg-background/80 p-2.5 shadow-sm">
                      <div className="mb-2 flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className="mt-1 inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                          <div className="text-xs font-medium leading-5">{item.name}</div>
                        </div>
                        <div className="text-xs font-semibold">{item.value}</div>
                      </div>
                      <div className="h-2 rounded-full bg-muted">
                        <div
                          className="h-2 rounded-full transition-all"
                          style={{ width: `${Math.max(percentage, 4)}%`, backgroundColor: item.color }}
                        />
                      </div>
                      <div className="mt-1 text-right text-xs text-muted-foreground">{percentage.toFixed(1)}%</div>
                    </div>
                  );
                })
              )}
              {data.length > compactData.length ? (
                <div className="pt-1 text-xs text-muted-foreground">
                  Showing top {compactData.length} segments out of {data.length}.
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
